import * as React from "react";

import { MuiDivider } from "@/components/ui/mui-divider";
import { MuiSkeleton } from "@/components/ui/mui-skeleton";
import { PageContainer } from "@/components/ui/page-container";
import { SpecScrollRegionContext } from "@/hooks/spec-lines";
import { cn } from "@/lib/utils";

// The edit page's frame — the donor's `spec-edit-page.tsx` grid round the
// Editor's island (`SpecEditIsland/components/Layout.tsx`) and the Item Search
// island beside it — and the Editor's own skeletons (`components/
// Skeletons.tsx`) (#806). An edit frame beside the details frame
// (`spec-details-frame.tsx`), not a variant of it: the two pages' geometry
// differs from the container in.
//
//   - **the page**: `PageContainer` (24px / 40px); inside it a wrapping row
//     (`Grid container`) that, from 1200px, is held to the viewport beneath
//     the app header and the spec band, its overflow hidden — the donor's
//     `calc(100vh - var(--app-header-height))`, whose one variable measured a
//     bar holding both. Below 1200px it has no max height and the page scrolls;
//   - **the left pane** (`Layout.tsx:10-18`): full width, then 7/12 from
//     1200px and 6/12 from 1700px; white; `pl` 16 → 32 and `pr` 16 → 0 from
//     1200px, `pb` 40 from 900px; from 1200px its own `overflow: auto` under
//     the same clamp. It is the Editor's **scroll region**: the sticky group
//     headers and the pagination observer's root bind to it through
//     `SpecScrollRegionContext`. Its items stand 16px apart (the island's
//     `Grid container spacing={2}`). The edit island has no scroll-to-top
//     button — only the details island does;
//   - **the divider**: MUI's vertical `flexItem` `Divider`, 1px of
//     `palette.divider`. Below 1200px it sits alone on a line of no height,
//     so it is drawn from 1200px only;
//   - **the right pane** (`ItemSearchIsland/components/Layout.tsx:10-21`):
//     full width, growing into the rest from 1200px with its own overflow — an
//     empty, labelled slot until the Item Search island host (#811) fills it.

/** The clamp both the grid and the left pane share from 1200px. */
const CLAMP =
  "min-[1200px]:max-h-[calc(100vh-var(--app-header-height,0px)-var(--spec-header-height,0px))]";

interface SpecEditFrameProps {
  /** The left pane: the island, or its skeleton. */
  children: React.ReactNode;
  /** The right pane's content — #811's island. */
  rightPane?: React.ReactNode;
}

export function SpecEditFrame({ children, rightPane }: SpecEditFrameProps) {
  const [region, setRegion] = React.useState<HTMLDivElement | null>(null);
  return (
    <PageContainer>
      <div
        id="edit-specs"
        data-testid="spec-edit-frame"
        className={cn("flex flex-row flex-wrap min-[1200px]:overflow-hidden", CLAMP)}
      >
        <div
          ref={setRegion}
          data-testid="spec-edit-scroll"
          className={cn(
            "text-lennar-ink flex w-full flex-col gap-4 bg-white px-4 min-[900px]:pb-10",
            "min-[1200px]:w-7/12 min-[1200px]:pr-0 min-[1200px]:pl-8 min-[1700px]:w-6/12",
            "min-[1200px]:overflow-auto",
            CLAMP,
          )}
        >
          <SpecScrollRegionContext.Provider value={region}>
            {children}
          </SpecScrollRegionContext.Provider>
        </div>
        <MuiDivider
          orientation="vertical"
          data-testid="spec-edit-divider"
          className="hidden min-[1200px]:block"
        />
        <section
          id="products-container"
          aria-label="Item Search"
          data-testid="spec-edit-right-pane"
          className="flex w-full min-w-0 flex-col min-[1200px]:w-auto min-[1200px]:flex-1 min-[1200px]:basis-0 min-[1200px]:overflow-auto"
        >
          {rightPane}
        </section>
      </div>
    </PageContainer>
  );
}

/**
 * `HeaderSkeleton` (`Skeletons.tsx:62-71`): a column, gap 12, `pt 24 pb 16`,
 * of five rounded wave bars — one fewer than the details page's.
 */
function SpecEditHeaderSkeleton() {
  const bars = ["h-6 w-1/2", "h-12 w-1/3", "h-10 w-[96%]", "h-14 w-[96%]", "h-6 w-[10%]"];
  return (
    <div data-testid="spec-edit-header-skeleton" className="flex w-full flex-col gap-3 pt-6 pb-4">
      {/* A fixed list, and two bars share a size: the index is the identity. */}
      {bars.map((size, index) => (
        <MuiSkeleton key={index} variant="rounded" animation="wave" className={size} />
      ))}
    </div>
  );
}

/**
 * The Editor's `GroupSkeleton` (`Skeletons.tsx:73-91`) — its own, not the
 * details page's: a 120px rounded `grey.300` block at MUI's default pulse, then
 * two rows of three 80px rectangular wave blocks on `grey.100` at 35 / 35 /
 * 30%, 4px apart and 4px under each row.
 */
export function SpecEditGroupSkeleton() {
  return (
    <div data-testid="spec-edit-group-skeleton" className="flex w-full flex-col">
      <MuiSkeleton variant="rounded" className="bg-lennar-grey-300 h-[120px] w-full" />
      {[0, 1].map((row) => (
        <div key={row} className="mb-1 flex flex-row gap-1">
          {/* A fixed list whose widths repeat: the index is the identity. */}
          {["w-[35%]", "w-[35%]", "w-[30%]"].map((width, index) => (
            <MuiSkeleton
              key={index}
              variant="rectangular"
              animation="wave"
              className={cn("bg-mui-grey-100 h-20", width)}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * `IslandSkeleton` (`Skeletons.tsx:5-16`): the header's bars and five group
 * skeletons, 16px apart — the whole left pane until the first lines page
 * lands, the right pane drawn beside it.
 */
export function SpecEditIslandSkeleton() {
  return (
    <div data-testid="spec-edit-skeleton" className="flex w-full flex-col gap-4">
      <SpecEditHeaderSkeleton />
      {[0, 1, 2, 3, 4].map((index) => (
        <SpecEditGroupSkeleton key={index} />
      ))}
    </div>
  );
}

/**
 * `LineSearchSkeleton` and `LinePaginationSkeleton`'s drawing
 * (`Skeletons.tsx:18-60`): two group skeletons, 32px apart.
 */
export const SpecEditSkeletonPair = React.forwardRef<HTMLDivElement, { testId: string }>(
  function SpecEditSkeletonPair({ testId }, ref) {
    return (
      <div ref={ref} data-testid={testId} className="flex w-full flex-col gap-8">
        <SpecEditGroupSkeleton />
        <SpecEditGroupSkeleton />
      </div>
    );
  },
);
